import React, { memo, useEffect, useRef, useState } from 'react';
import { Avatar, Box, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Mic, MicOff, Videocam, VideocamOff } from '@mui/icons-material/';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

import { COUNTDOWN_CAMERA, FAKE_STREAM } from './../../common/constants';
import {
	characterState,
	microOnState,
	userNameState,
	videoOnState,
} from './../../common/recoil/UserInfo/atoms';
import { remoteNamesState } from './../../common/recoil/websocket/atoms';
import {
	currentCameraLayoutState,
	isShareHighlightedState,
} from './../../common/recoil/Cameras/atom';
import { customDiceSelectedState } from '../../common/recoil/GameSystem/gameSystemAtom';
import { CountDown, DiceIcons, DiceResult, DiceResultAlert, StreamMenu } from './../../components';
import { IS_LOCALHOST } from './../../common/constants';
import { getDiceColorPreset } from './../../common/services/diceMetadata';
import { updateCharacter } from './../../common/services/characterRequests';
import UserCameraCustomDices from './UserCameraCustomDices';
import CameraStatusMetrics from './CameraStatusMetrics';
import { debugSession } from '../../common/utils/sessionDebug';
import { getAlienPanicEntry } from '../../common/gameSystems/alien/panicTable';

const useStyles = makeStyles((theme) => ({
	cameraWrapper: {
		height: '100%',
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'relative',
		overflow: 'hidden',
	},
	cameraWrapperAlien: {
		background:
			'radial-gradient(circle at center, rgba(18, 33, 28, 0.26) 0%, rgba(6, 11, 10, 0.52) 100%)',
		boxShadow:
			'inset 0 0 0 1px rgba(120, 146, 135, 0.34), inset 0 0 56px rgba(118, 184, 143, 0.16)',
	},
	cameraBorder: {
		background: 'blue',
	},
	video: {
		// maxWidth: '100%',
		// maxHeight: '100%',
	},
	videoAlien: {
		filter: 'saturate(0.74) contrast(1.1) brightness(0.86) hue-rotate(-10deg)',
	},
	alienOverlay: {
		position: 'absolute',
		pointerEvents: 'none',
		background:
			'linear-gradient(180deg, rgba(157, 214, 186, 0.22) 0%, rgba(51, 105, 83, 0.1) 28%, rgba(13, 38, 29, 0.12) 62%, rgba(8, 20, 17, 0.38) 100%)',
		mixBlendMode: 'screen',
		opacity: 1,
	},
	alienEmergencyOverlay: {
		position: 'absolute',
		pointerEvents: 'none',
		background:
			'radial-gradient(circle at center, rgba(255, 132, 132, 0.12) 0%, rgba(164, 18, 18, 0.22) 52%, rgba(80, 0, 0, 0.42) 100%)',
		mixBlendMode: 'screen',
		boxShadow:
			'inset 0 0 48px rgba(255, 72, 72, 0.24), inset 0 0 140px rgba(120, 0, 0, 0.32)',
		animation: '$alienEmergencyPulse 2200ms ease-in-out infinite',
	},
	'@keyframes alienEmergencyPulse': {
		'0%': {
			opacity: 0.5,
		},
		'50%': {
			opacity: 0.92,
		},
		'100%': {
			opacity: 0.5,
		},
	},
	alienGrid: {
		position: 'absolute',
		pointerEvents: 'none',
		backgroundImage:
			'linear-gradient(rgba(168, 208, 189, 0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(168, 208, 189, 0.05) 1px, transparent 1px)',
		backgroundSize: '100% 3px, 6px 100%',
		opacity: 0.22,
	},
	alienCorners: {
		position: 'absolute',
		pointerEvents: 'none',
		border: '1px solid rgba(118, 140, 131, 0.32)',
		boxShadow:
			'inset 0 0 0 1px rgba(24, 36, 32, 0.7), inset 0 0 22px rgba(91, 147, 121, 0.08)',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: 10,
			left: 10,
			width: 34,
			height: 34,
			borderTop: '3px solid rgba(194, 227, 212, 0.82)',
			borderLeft: '3px solid rgba(194, 227, 212, 0.82)',
			boxShadow: '-2px -2px 16px rgba(153, 214, 186, 0.16)',
		},
		'&::after': {
			content: '""',
			position: 'absolute',
			right: 10,
			bottom: 10,
			width: 34,
			height: 34,
			borderRight: '3px solid rgba(194, 227, 212, 0.82)',
			borderBottom: '3px solid rgba(194, 227, 212, 0.82)',
			boxShadow: '2px 2px 16px rgba(153, 214, 186, 0.16)',
		},
	},
	alienBottomBar: {
		position: 'absolute',
		pointerEvents: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '6px 12px',
		background:
			'linear-gradient(90deg, rgba(8, 15, 14, 0.92) 0%, rgba(14, 24, 21, 0.82) 100%)',
		borderTop: '1px solid rgba(166, 198, 184, 0.3)',
		boxShadow: '0 -10px 24px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(224, 239, 231, 0.05)',
	},
	alienBottomTag: {
		fontFamily: '"Oswald", "Roboto Condensed", sans-serif',
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: '0.22em',
		textTransform: 'uppercase',
		color: '#d7e3dc',
	},
	alienBottomAccent: {
		width: 72,
		height: 6,
		background:
			'linear-gradient(90deg, rgba(240, 191, 90, 0.95) 0%, rgba(240, 191, 90, 0.35) 100%)',
		boxShadow: '0 0 14px rgba(240, 191, 90, 0.3)',
	},
	infoArea: {
		//height: '100px',
		height: '20%',
		position: 'absolute',
		bottom: 0,
		background: 'white',
		//background: 'rgba(0, 0, 0, 0.2)',
	},
	inlineName: {
		position: 'absolute',
		color: 'white',
		textAlign: 'center',
		backgroundImage: 'url(/images/name-background.png)',
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'center',
		backgroundSize: 'contain',
		height: '64px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginBottom: '10px',
	},
	inlineNameAlien: {
		backgroundImage: 'none',
		background: 'rgba(10, 16, 15, 0.86)',
		border: '1px solid rgba(122, 145, 135, 0.34)',
		boxShadow:
			'0 10px 18px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(220, 237, 229, 0.06)',
		clipPath:
			'polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px))',
		height: '58px',
		padding: '0 22px',
	},
	inlineNameSideBar: {
		position: 'absolute',
		color: 'white',
		textAlign: 'center',
		backgroundImage: 'url(/images/name-background.png)',
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'center',
		backgroundSize: 'contain',
		height: '25px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginBottom: '5px',
	},
	inlineNameSideBarAlien: {
		backgroundImage: 'none',
		background: 'rgba(10, 16, 15, 0.88)',
		border: '1px solid rgba(122, 145, 135, 0.34)',
		clipPath:
			'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
		height: '24px',
		padding: '0 10px',
	},
	inlineAvatar: {
		position: 'absolute',
		margin: theme.spacing(2),
	},
	inlineDices: {
		position: 'absolute',
		'&::-webkit-scrollbar': {
			width: '0.4em',
			height: '0.1px',
		},
		'&::-webkit-scrollbar-track': {
			boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
			webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(0,0,0,.3)',
			outline: '1px solid rgba(255,255,255,.3)',
		},
		overflow: 'auto',
		height: '100%',
		display: 'flex',
		flexDirection: 'column-reverse',
	},
	inlineMediaStreamButtonsBig: {
		background: 'black',
		color: 'white',
		marginTop: '5px',
		marginLeft: '5px',
	},
	inlineMediaStreamButtonsBigAlien: {
		background: 'rgba(10, 16, 15, 0.88)',
		color: '#d7e3dc',
		border: '1px solid rgba(122, 145, 135, 0.34)',
		boxShadow: 'inset 0 1px 0 rgba(220, 237, 229, 0.06)',
	},
	inlineMediaStreamButtonsSmall: {
		background: 'black',
		color: 'white',
		marginTop: '5px',
		marginLeft: '5px',
		width: '5px',
		height: '5px',
		'& svg': {
			width: '20px',
			height: '20px',
		},
	},
	inlineMediaStreamButtonsSmallAlien: {
		background: 'rgba(10, 16, 15, 0.9)',
		color: '#d7e3dc',
		border: '1px solid rgba(122, 145, 135, 0.34)',
		boxShadow: 'inset 0 1px 0 rgba(220, 237, 229, 0.06)',
	},
	dice: {
		background: "url('/images/d10.png') no-repeat center transparent",
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'center',
		height: '50px',
		width: '100px',
		backgroundSize: 'contain',
		color: 'white',
	},
	newDice: {
		position: 'relative',
	},
	newDiceSvg: {
		color: 'black',
	},
	newDiceSpan: {
		color: 'white',
		background: 'black',
		position: 'absolute',
		fontSize: '16px',
		left: '10px',
		top: '10px',
		width: '19px',
		textAlign: 'center',
		borderRadius: '50px',
	},
	sidebarAvatar: {
		width: theme.spacing(5),
		height: theme.spacing(5),
	},
	avatar: {
		width: theme.spacing(10),
		height: theme.spacing(10),
		marginBottom: '5px',
	},
	h1Name: {
		fontSize: '40px',
		fontFamily: "'Amatic SC',	cursive",
		//fontFamily: "'Architects Daughter', cursive",
		//fontFamily: "'Teko', sans-serif",
	},
	h1NameAlien: {
		fontSize: '24px',
		fontFamily: '"Oswald", "Roboto Condensed", sans-serif',
		fontWeight: 700,
		letterSpacing: '0.18em',
		textTransform: 'uppercase',
		color: '#d7e3dc',
	},
	h1NameSidebar: {
		fontSize: '20px',
		fontFamily: "'Amatic SC',	cursive",
		//fontFamily: "'Architects Daughter', cursive",
		//fontFamily: "'Teko', sans-serif",
	},
	h1NameSidebarAlien: {
		fontSize: '12px',
		fontFamily: '"Oswald", "Roboto Condensed", sans-serif',
		fontWeight: 700,
		letterSpacing: '0.12em',
		textTransform: 'uppercase',
		color: '#d7e3dc',
	},
	connectionBadge: {
		position: 'absolute',
		left: 8,
		bottom: 8,
		zIndex: 2,
		padding: '4px 8px',
		fontSize: 10,
		fontWeight: 700,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		background: 'rgba(10, 16, 15, 0.92)',
		border: '1px solid rgba(122, 145, 135, 0.34)',
		color: '#d2e0d8',
	},
	connectionBadgeWarning: {
		color: '#f0bf5a',
		borderColor: 'rgba(240, 191, 90, 0.5)',
	},
	connectionBadgeError: {
		color: '#ff8e8e',
		borderColor: 'rgba(217, 74, 74, 0.6)',
	},
}));

const UserCameraContainer = ({
	avatarUrl,
	diceResults,
	mediaStream,
	userId,
	isShare,
	isLocalUser,
	remoteUserName,
	statusMetrics = [],
	connectionHealth = null,
	isAlienTheme = false,
	useCameraClass = 'camera',
	isSideBar = false,
	videoPlaying = true,
	isMicPlaying = true,
}) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const videoRef = useRef();
	const wrapperRef = useRef();

	const [microOn, setMicroOn] = useRecoilState(microOnState);
	const [videoOn, setVideoOn] = useRecoilState(videoOnState);
	const [localCharacter, setLocalCharacter] = useRecoilState(characterState);
	const [showDebugPanicOverlay, setShowDebugPanicOverlay] = useState(false);
	const [debugPanicValue, setDebugPanicValue] = useState(0);
	const [isFake, setIsFake] = useState(false);
	const [isCountdown, setIsCountDown] = useState(false);
	const [cameraTopPosition, setCamperaTopPosition] = useState(0);
	const [cameraBottomPosition, setCamperaBottomPosition] = useState(0);
	const [cameraLeftPosition, setCamperaLeftPosition] = useState(0);
	const [cameraRightPosition, setCamperaRgihtPosition] = useState(0);
	const [cameraWidth, setCameraWidth] = useState('100%');
	const [cameraHeight, setCameraHeight] = useState('100%');
	const [infoWidth, setInfoWidth] = useState('100%');
	const [avatarBigWidth, setAvatarBigWidth] = useState('100%');
	const userName = useRecoilValue(userNameState);
	const remoteNames = useRecoilValue(remoteNamesState);
	const isShareHighlighted = useRecoilValue(isShareHighlightedState);
	const currentCameraLayout = useRecoilValue(currentCameraLayoutState);
	const customDiceSelected = useRecoilValue(customDiceSelectedState);
	const isAlienCamera = isAlienTheme || statusMetrics.some((metric) =>
		String(metric?.labelKey || '').startsWith('cameraMetrics.alien.')
	);
	const isAlienAirDepleted = statusMetrics.some(
		(metric) => metric?.key === 'air' && Number(metric?.value) <= 0
	);
	const activePanicMetric = statusMetrics.find((metric) => metric?.key === 'active_panic') || null;
	const views = {
		view1: {
			info: 'inside',
		},
		view2: {
			info: 'outside',
		},
		view3: {
			info: 'inline',
		},
	};

	const selectedView = views.view3;

	const cameraBoundsStyle = {
		top: cameraTopPosition || 0,
		left: cameraLeftPosition || 0,
		width: infoWidth || '100%',
		height: cameraHeight || '100%',
	};

	useEffect(() => {
		if (showDebugPanicOverlay && activePanicMetric) {
			setDebugPanicValue(Number(activePanicMetric.value || 0));
		}
	}, [showDebugPanicOverlay, activePanicMetric?.value]);

	const displayedStatusMetrics = React.useMemo(() => {
		if (!showDebugPanicOverlay || !isAlienCamera) {
			return statusMetrics;
		}

		const metrics = [...statusMetrics];
		const activePanicIndex = metrics.findIndex((metric) => metric?.key === 'active_panic');
		const panicValue =
			activePanicIndex >= 0
				? Number(metrics[activePanicIndex].value || 0)
				: Number(debugPanicValue || 0);
		const debugMetric = {
			key: 'active_panic',
			kind: 'alienActivePanic',
			labelKey: 'cameraMetrics.alien.panic',
			value: panicValue,
			tone: panicValue >= 10 ? 'danger' : panicValue >= 7 ? 'warning' : 'neutral',
			icon: 'panic',
			editable: true,
			min: 0,
			max: 15,
			panicEffectId: panicValue > 0 ? getAlienPanicEntry(Math.max(1, panicValue)).id : null,
			title: panicValue <= 0 ? t('cameraMetrics.alien.noActivePanic') : null,
			description: '',
			debugVisible: true,
		};

		if (activePanicIndex >= 0) {
			metrics[activePanicIndex] = {
				...metrics[activePanicIndex],
				...debugMetric,
				panicEffectId: metrics[activePanicIndex].panicEffectId || debugMetric.panicEffectId,
			};
			return metrics;
		}

		return [debugMetric, ...metrics];
	}, [showDebugPanicOverlay, isAlienCamera, statusMetrics, debugPanicValue, t]);

	const updatePositions = () => {
		const streamWidth = mediaStream?.getVideoTracks()[0]?.getSettings()?.width;
		const streamHeight = mediaStream?.getVideoTracks()[0]?.getSettings()?.height;
		const containerWidth = wrapperRef?.current?.clientWidth || 0;
		const containerHeight = wrapperRef?.current?.clientHeight || 0;

		const newHeight = (containerWidth / streamWidth) * streamHeight;
		const newWidth = (containerHeight / streamHeight) * streamWidth;

		if (containerHeight > newHeight) {
			setCamperaTopPosition(`${Math.abs(containerHeight - newHeight) / 2}px`);
		} else {
			setCamperaTopPosition(0);
		}

		setAvatarBigWidth(`${Math.min(newHeight, newWidth) / 2}px`);

		if (isSideBar) {
			setInfoWidth('100%');
			setCamperaLeftPosition('0');
			const newInfoWidth = Math.min(newWidth, containerWidth);

			setInfoWidth(`${newInfoWidth}px`);
			setCamperaLeftPosition(`${(containerWidth - newInfoWidth) / 2}px`);
		} else if (streamWidth) {
			const newInfoWidth = Math.min(newWidth, containerWidth);

			setInfoWidth(`${newInfoWidth}px`);
			setCamperaLeftPosition(`${(containerWidth - newInfoWidth) / 2}px`);
		}
	};

	useEffect(() => {
		if (
			mediaStream &&
			mediaStream.type !== FAKE_STREAM &&
			mediaStream.type !== COUNTDOWN_CAMERA
		) {
			videoRef.current.srcObject = mediaStream;
			//updateSize();
			updatePositions();
			try {
				mediaStream.oninactive = () => {
					console.log(`end streaming ${userId}`);
				};
			} catch (e) {
				console.error('Error setting oninactive mediastream', e);
			}

			if (IS_LOCALHOST && isLocalUser) {
				stopCamera();
				stopAudio();
			}
			setInterval(updatePositions, 10000);
		} else if (mediaStream && mediaStream.type === FAKE_STREAM) {
			setIsFake(true);
		} else if (mediaStream && mediaStream.type === COUNTDOWN_CAMERA) {
			setIsCountDown(true);
		}
	}, [mediaStream]);

	// useEffect(() => {
	// 	const observer = new ResizeObserver((entries) => {
	// 		if (mediaStream) {
	// 			updateSize();
	// 		}
	// 	});
	// 	observer.observe(wrapperRef.current);
	// 	return () => wrapperRef.current && observer.unobserve(wrapperRef.current);
	// }, []);

	useEffect(() => {
		if (mediaStream && mediaStream.type !== COUNTDOWN_CAMERA) {
			//updateSize();
			updatePositions();
			setTimeout(updatePositions, 1);
		}
	}, [remoteNames, isSideBar, currentCameraLayout, isShareHighlighted]);

	const handleCanPlay = () => {
		videoRef.current.play();
	};

	const stopCamera = () => {
		mediaStream.getVideoTracks()[0].enabled = !mediaStream.getVideoTracks()[0].enabled;
		setVideoOn(!videoOn);
	};

	const stopAudio = () => {
		mediaStream.getAudioTracks()[0].enabled = !mediaStream.getAudioTracks()[0].enabled;
		setMicroOn(!microOn);
	};

	const renderInfo = () => {
		const renderDie = (die, key) => {
			const colorPreset = getDiceColorPreset(die.color);
			return (
				<Box className={classes.newDice} key={key} title={die.label || 'default'}>
					<DiceIcons
						dice={die.notation}
						size="medium"
						color={colorPreset.iconColor}
					/>
					<span
						className={classes.newDiceSpan}
						style={{
							background: colorPreset.badgeBackground,
							color: colorPreset.badgeColor,
						}}>
						<DiceResult notation={die.notation} result={die.value} type={die.type} />
					</span>
				</Box>
			);
		};

		return (
			<Box width={infoWidth} className={classes.infoArea}>
				<h1>{isLocalUser ? userName : remoteUserName}</h1>
				{avatarUrl && (
					<Avatar
						className={isSideBar ? classes.sidebarAvatar : classes.avatar}
						src={avatarUrl}
					/>
				)}
				<Box>
					{diceResults?.map((result, index) => {
						if (result) {
							if (Array.isArray(result)) {
								return result.map((x, subIndex) => {
									return renderDie(x, `dice-${index}-${subIndex}`);
								});
							} else if (result instanceof Object) {
								return renderDie(result, `dice-${index}`);
							} else {
								return <Box className={classes.dice}> {result} </Box>;
							}
						}
					})}
				</Box>
				<DiceResultAlert
					results={diceResults}
					isLocalUser={isLocalUser}
					userName={isLocalUser ? userName : remoteUserName}></DiceResultAlert>
			</Box>
		);
	};

	const renderInlineInfo = () => {
		return (
			<>
				{(!isLocalUser || debugMenuOptions.length > 0) && (
					<StreamMenu
						overridenTop={cameraTopPosition}
						overridenRight={cameraLeftPosition}
						userId={userId}
						debugOption={debugMenuOptions[0] || null}
					/>
				)}
				<Box
					bottom={cameraTopPosition}
					className={`${isSideBar ? classes.inlineNameSideBar : classes.inlineName} ${
						isAlienCamera
							? isSideBar
								? classes.inlineNameSideBarAlien
								: classes.inlineNameAlien
							: ''
					}`}
					width={infoWidth}>
					<h1
						className={`${isSideBar ? classes.h1NameSidebar : classes.h1Name} ${
							isAlienCamera
								? isSideBar
									? classes.h1NameSidebarAlien
									: classes.h1NameAlien
								: ''
						}`}
						m={0}>
						{isLocalUser ? userName : remoteUserName}
					</h1>
				</Box>

				{avatarUrl && videoPlaying && (
					<Box
						bottom={cameraTopPosition}
						right={cameraLeftPosition}
						className={classes.inlineAvatar}>
						<Avatar
							className={isSideBar ? classes.sidebarAvatar : classes.avatar}
							src={avatarUrl}
						/>
					</Box>
				)}
				<Box left={cameraLeftPosition} className={classes.inlineDices}>
					<UserCameraCustomDices diceResults={diceResults} />
				</Box>
				<CameraStatusMetrics
					metrics={displayedStatusMetrics}
					isSideBar={isSideBar}
					overridenTop={cameraTopPosition}
					overridenRight={cameraLeftPosition}
					editable={isLocalUser}
					onMetricAdjust={handleMetricAdjust}
					onMetricClear={handleMetricClear}
				/>
				<Box top={cameraTopPosition} position="absolute">
					<IconButton
						className={`${
							isSideBar
								? classes.inlineMediaStreamButtonsSmall
								: classes.inlineMediaStreamButtonsBig
						} ${
							isAlienCamera
								? isSideBar
									? classes.inlineMediaStreamButtonsSmallAlien
									: classes.inlineMediaStreamButtonsBigAlien
								: ''
						}`}
						color="secondary"
						aria-label="upload picture"
						component="span"
						onClick={() => {
							if (isLocalUser) stopAudio();
						}}
						size="large">
						{isMicPlaying ? <Mic></Mic> : <MicOff></MicOff>}
					</IconButton>
					{isLocalUser && (
						<IconButton
							className={`${
								isSideBar
									? classes.inlineMediaStreamButtonsSmall
									: classes.inlineMediaStreamButtonsBig
							} ${
								isAlienCamera
									? isSideBar
										? classes.inlineMediaStreamButtonsSmallAlien
										: classes.inlineMediaStreamButtonsBigAlien
									: ''
							}`}
							color="secondary"
							aria-label="upload picture"
							component="span"
							onClick={() => {
								stopCamera();
							}}
							size="large">
							{isLocalUser &&
								(videoOn ? <Videocam></Videocam> : <VideocamOff></VideocamOff>)}
						</IconButton>
					)}
				</Box>
				<DiceResultAlert
					results={diceResults}
					isLocalUser={isLocalUser}
					userName={isLocalUser ? userName : remoteUserName}></DiceResultAlert>
				{connectionHealth?.status && connectionHealth.status !== 'healthy' && (
					<Box
						className={`${classes.connectionBadge} ${
							connectionHealth.status === 'error'
								? classes.connectionBadgeError
								: classes.connectionBadgeWarning
						}`}>
						{connectionHealth.message}
					</Box>
				)}
			</>
		);
	};

	const getMetricElementMatcher = (metric) => (element) => {
		if (metric?.sourceKey && element?.key === metric.sourceKey) {
			return true;
		}

		if (
			Array.isArray(metric?.sourceLabels) &&
			metric.sourceLabels.some(
				(label) => String(element?.label || '').toLowerCase() === String(label).toLowerCase()
			)
		) {
			return true;
		}

		return element?.key === metric?.key;
	};

	const getMetricNumericValue = (value) => {
		if (typeof value === 'number') {
			return value;
		}

		if (typeof value === 'string') {
			const parsed = parseInt(value, 10);
			return Number.isNaN(parsed) ? 0 : parsed;
		}

		if (value && typeof value === 'object') {
			return value.value || 0;
		}

		return 0;
	};

	const applyMetricValue = (currentValue, nextValue) => {
		if (currentValue && typeof currentValue === 'object') {
			return {
				...currentValue,
				value: nextValue,
			};
		}

		return String(nextValue);
	};

	const toggleDebugAlienPanic = () => {
		if (!isLocalUser || !isAlienCamera) {
			return;
		}

		setShowDebugPanicOverlay((current) => {
			const next = !current;
			if (next) {
				setDebugPanicValue(Number(activePanicMetric?.value || 0));
			}
			return next;
		});
	};

	const debugMenuOptions =
		isLocalUser && isAlienCamera
			? [
					{
						title: t('streamMenu.debug'),
						action: toggleDebugAlienPanic,
					},
			  ]
			: [];

	const buildUpdatedAlienPanicState = (character, nextValue) => {
		if (nextValue < 7) {
			return {
				...(character.systemState || {}),
				alien: {
					...(character.systemState?.alien || {}),
					activePanicResult: null,
					activePanicEffect: null,
				},
			};
		}

		const panicEffect = getAlienPanicEntry(nextValue);

		return {
			...(character.systemState || {}),
			alien: {
				...(character.systemState?.alien || {}),
				activePanicResult: nextValue,
				activePanicEffect: {
					id: panicEffect.id,
				},
			},
		};
	};

	const persistUpdatedCharacter = async (updatedCharacter, debugMeta = {}) => {
		setLocalCharacter(updatedCharacter);

		try {
			await updateCharacter(updatedCharacter.ID, updatedCharacter);
			debugSession('UserCameraContainer', 'metric-adjust-success', {
				characterId: updatedCharacter.ID,
				...debugMeta,
			});
		} catch (error) {
			debugSession('UserCameraContainer', 'metric-adjust-error', {
				characterId: updatedCharacter.ID,
				message: error?.message || null,
				...debugMeta,
			});
			console.error('Error updating camera metric character value', error);
		}
	};

	const handleMetricAdjust = async (metric, delta) => {
		debugSession('UserCameraContainer', 'metric-adjust-start', {
			metricKey: metric?.key || null,
			metricLabel: metric?.labelKey || null,
			delta,
			isLocalUser,
			characterId: localCharacter?.ID || null,
		});
		if (!isLocalUser || !localCharacter?.ID || !metric?.editable) {
			debugSession('UserCameraContainer', 'metric-adjust-skipped', {
				metricKey: metric?.key || null,
				delta,
				isLocalUser,
				characterId: localCharacter?.ID || null,
				editable: metric?.editable || false,
			});
			return;
		}

		if (metric?.kind === 'alienActivePanic') {
			const min = typeof metric.min === 'number' ? metric.min : 7;
			const max = typeof metric.max === 'number' ? metric.max : 15;
			const currentValue = Number(metric.value || 0);
			const nextValue = Math.min(max, Math.max(min - 1, currentValue + delta));
			if (metric?.debugVisible) {
				setDebugPanicValue(nextValue);
			}

			if (nextValue >= 7) {
				const updatedCharacter = {
					...localCharacter,
					systemState: buildUpdatedAlienPanicState(localCharacter, nextValue),
				};

				await persistUpdatedCharacter(updatedCharacter, {
					metricKey: metric?.key || null,
					delta,
					mode: 'active-panic',
				});
			} else if (Number(localCharacter?.systemState?.alien?.activePanicResult || 0) >= 7) {
				const updatedCharacter = {
					...localCharacter,
					systemState: buildUpdatedAlienPanicState(localCharacter, 0),
				};

				await persistUpdatedCharacter(updatedCharacter, {
					metricKey: metric?.key || null,
					delta,
					mode: 'active-panic-clear-below-seven',
				});
			}
			return;
		}

		let didUpdateElement = false;
		const updatedSections = (localCharacter.sections || []).map((section) => ({
			...section,
			elements: (section.elements || []).map((element) => {
				if (!getMetricElementMatcher(metric)(element)) {
					return element;
				}

				const currentValue = getMetricNumericValue(element.value);
				const min = typeof metric.min === 'number' ? metric.min : Number.NEGATIVE_INFINITY;
				const max = typeof metric.max === 'number' ? metric.max : Number.POSITIVE_INFINITY;
				const nextValue = Math.min(max, Math.max(min, currentValue + delta));
				didUpdateElement = true;

				return {
					...element,
					value: applyMetricValue(element.value, nextValue),
				};
			}),
		}));

		if (!didUpdateElement) {
			debugSession('UserCameraContainer', 'metric-adjust-no-match', {
				metricKey: metric?.key || null,
				delta,
			});
			return;
		}

		const updatedCharacter = {
			...localCharacter,
			sections: updatedSections,
		};

		await persistUpdatedCharacter(updatedCharacter, {
			metricKey: metric?.key || null,
			delta,
		});
	};

	const handleMetricClear = async (metric) => {
		if (!isLocalUser || !localCharacter?.ID || metric?.kind !== 'alienActivePanic') {
			return;
		}

		if (showDebugPanicOverlay || metric?.debugVisible) {
			setDebugPanicValue(0);
		}

		const updatedCharacter = {
			...localCharacter,
			systemState: buildUpdatedAlienPanicState(localCharacter, 0),
		};

		await persistUpdatedCharacter(updatedCharacter, {
			metricKey: metric?.key || null,
			mode: 'active-panic-clear',
		});
	};

	return (
		<Box className={useCameraClass} display="flex" justifyContent="center">
			<Box
				ref={wrapperRef}
				className={`${classes.cameraWrapper} ${isAlienCamera ? classes.cameraWrapperAlien : ''}`}>
				{/* <Box className={classes.border}>
					<video
						className={classes.video}
						ref={videoRef}
						onCanPlay={handleCanPlay}
						autoPlay
						playsInline
						muted={isLocalUser === true}
					/>
				</Box> */}
				{isCountdown ? (
					<CountDown />
				) : (
					<>
						<video
							height={cameraHeight}
							width={cameraWidth}
							className={`${classes.video} ${isAlienCamera ? classes.videoAlien : ''}`}
							ref={videoRef}
							onCanPlay={handleCanPlay}
							autoPlay
							playsInline
							muted={isLocalUser === true}
						/>
						{!videoPlaying && avatarUrl && (
							<Box
								position="absolute"
								width={avatarBigWidth}
								height={avatarBigWidth}
								borderRadius="50%"
								display="flex"
								overflow="hidden"
								style={{
									backgroundImage: `url(${avatarUrl})`,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
								}}>
								{/* <img src={avatarUrl} />{' '} */}
							</Box>
						)}
						{isAlienCamera && (
							<Box className={classes.alienOverlay} style={cameraBoundsStyle} />
						)}
						{isAlienCamera && isAlienAirDepleted && (
							<Box className={classes.alienEmergencyOverlay} style={cameraBoundsStyle} />
						)}
						{isAlienCamera && (
							<Box className={classes.alienGrid} style={cameraBoundsStyle} />
						)}
						{isAlienCamera && (
							<Box className={classes.alienCorners} style={cameraBoundsStyle} />
						)}
						{isAlienCamera && (
							<Box
								className={classes.alienBottomBar}
								style={{
									left: cameraBoundsStyle.left,
									width: cameraBoundsStyle.width,
									bottom: cameraTopPosition || 0,
								}}>
								<span className={classes.alienBottomTag}>MU/TH/UR LINK</span>
								<span className={classes.alienBottomAccent}></span>
							</Box>
						)}
						{selectedView.info === 'inside' && renderInfo()}
						{selectedView.info === 'inline' && renderInlineInfo()}
					</>
				)}
			</Box>
			{selectedView.info === 'outside' && renderInfo()}
		</Box>
	);
};

export default UserCameraContainer;
